import Image from "next/image";
import Link from "next/link";

import { isDismissedToday } from "@/store/popup-banner-store";
import { useTactGloveVrchatPopupStore } from "@/store/tactglove-vrchat-popup-store";
import { useTrans } from "@/utils/useTrans";
import type { PopupSlide } from "@/v2-components/ui/floating-popup";

export const TACTGLOVE_VRCHAT_POPUP_BG =
  "/images/event/tactglove-vrchat-popup-bg.webp";
export const TACTGLOVE_DK3_PDP_PATH = "/shop/buy-tactsuit/tactglove-dk3/";

/**
 * 홈 팝업의 TactGlove DK3 VRChat 슬라이드 제공자 (#6025).
 *
 * GTA5(`useGta5PopupSlides`)와 같은 «슬라이드 제공자» 형태다 — 껍데기(`FloatingPopup`)는
 * 홈에 하나이고(`home-floating-popups.tsx`), 이 캠페인의 저장소·마크업만 여기 둔다.
 *
 * **기간이 없다**(작성자 확정 2026-10-16: 상시 노출). 그래서 서버 판정 prop 도 없다 —
 * 조건은 「오늘 안 닫았다」 하나다.
 */
export function useTactGloveVrchatPopupSlides(): PopupSlide[] {
  const { t } = useTrans(["event"]);
  const dismissedUntil = useTactGloveVrchatPopupStore((s) => s.dismissedUntil);
  const dismiss = useTactGloveVrchatPopupStore((s) => s.dismiss);

  if (isDismissedToday(dismissedUntil)) return [];

  return [
    {
      key: "tactglove-vrchat",
      label: t("vrchatPopupEyebrow"),
      onDismissToday: dismiss,
      node: <TactGloveVrchatSlide />,
    },
  ];
}

/**
 * 시안 `popup/tactglove-vrchat-popup.html`(#6025 첨부) — 360×190 슬롯 + 셸 푸터 40.
 *
 * ```
 *   배경   720×380 글자 없는 판(왼쪽 어둡게 · 오른쪽 캐릭터) · object-cover
 *   글     absolute top17 left16 w185
 *     eyebrow  10/14 500        #e4eaf4  mb6
 *     title    18/24 600 -.35   white
 *     desc     11/16 -.1        #d6dde7  mt6
 *     CTA      h34 px14 r18 bg#0060ff(hover #0055e5) 12/18 600 · mt11 · gap11 · 화살표 13
 * ```
 *
 * ★글자를 이미지에 굽지 않는다 — 7개 언어로 나간다. 폭 185 는 오른쪽 캐릭터를 피하는 값이다.
 */
function TactGloveVrchatSlide() {
  const { t } = useTrans(["event"]);

  return (
    <div className="relative h-[190px] bg-[#07101b]">
      <Image
        src={TACTGLOVE_VRCHAT_POPUP_BG}
        alt=""
        fill
        sizes="360px"
        className="object-cover"
        priority
      />
      <div className="absolute left-4 top-[17px] w-[185px]">
        <p className="mb-1.5 text-[10px] font-500 leading-[14px] text-[#e4eaf4]">
          {t("vrchatPopupEyebrow")}
        </p>
        <p className="whitespace-pre-line text-[18px] font-600 leading-6 tracking-[-0.35px] text-white">
          {t("vrchatPopupTitle")}
        </p>
        <p className="mt-1.5 text-[11px] leading-4 tracking-[-0.1px] text-[#d6dde7]">
          {t("vrchatPopupDescription")}
        </p>
        <Link
          href={TACTGLOVE_DK3_PDP_PATH}
          className="mt-[11px] inline-flex h-[34px] items-center gap-[11px] rounded-[18px] bg-[#0060ff] px-3.5 text-[12px] font-600 leading-[18px] text-white transition-colors hover:bg-[#0055e5]"
        >
          {t("vrchatPopupCta")}
          <svg
            aria-hidden="true"
            width="13"
            height="13"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          >
            <path d="M3 10h13m-5-5 5 5-5 5" />
          </svg>
        </Link>
      </div>
    </div>
  );
}
